import { useState, useMemo, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Drawer,
  Divider,
  message,
  Modal,
  Empty,
  Checkbox ,
} from "antd";

import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";

import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import Title from "antd/es/typography/Title";
import { getVendors,createVendor } from "../../api/vendorApi";

import { getExpenseCategories } from "../../api/expenseCategoryApi";
import {
  getAllExpense,
  createExpense,
  editExpense,
  deleteExpense,
} from "../../api/expenseApi";
import { canViewModule, canCreateModule, canUpdateModule, canDeleteModule } from "../utils/permissionUtils";

dayjs.extend(isBetween);

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export default function ExpensePage() {
  const MODULE_KEY = "in_house_expenses";
  const canView = canViewModule(MODULE_KEY);
  const canCreate = canCreateModule(MODULE_KEY);
  const canUpdate = canUpdateModule(MODULE_KEY);
  const canDelete = canDeleteModule(MODULE_KEY);
  const [expenses, setExpenses] = useState([]); 
  const [vendors, setVendors] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [open, setOpen] = useState(false); 
  const [editingExpense, setEditingExpense] = useState(null);
  const [items, setItems] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
 
  const [addItemModal, setAddItemModal] = useState(false);
  const [itemForm] = Form.useForm();

  //create vendor
  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [vendorForm] = Form.useForm();
  
  const [editItemIdx, setEditItemIdx] = useState(null);

  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    category: "all",
    dateRange: null,
  });

  const [form] = Form.useForm();
  const paymentStatus = Form.useWatch("status", form);
  const getVendorPrimaryId = (vendor) => vendor?.id ?? vendor?.vendor_id ?? null;

  const fetchExpenses = async (showMessage = false) => {
    if (!canView) return;
    try {
      setExpensesLoading(true);
      const res = await getAllExpense();
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setExpenses(list);
      if (showMessage) {
        message.success("Expenses loaded");
      }
    } catch (err) {
      console.error("EXPENSE FETCH ERROR:", err);
      if (showMessage) {
        message.error("Failed to load expenses");
      }
    } finally {
      setExpensesLoading(false);
    }
  };

  const fetchVendors = async (showMessage = false) => {
    if (!canView) return;
    try {
      setVendorsLoading(true);
      const res = await getVendors();
      const list = Array.isArray(res?.data) ? res.data : [];
      setVendors(list);
      if (showMessage) {
        message.success("Vendors loaded");
      }
    } catch (err) {
      console.error("VENDOR FETCH ERROR:", err);
      if (showMessage) {
        message.error("Failed to load vendors");
      }
    } finally {
      setVendorsLoading(false);
    }
  };

  

  const fetchExpenseCategories = async (showMessage = false) => {
    if (!canView) return;
    try {
      setCategoriesLoading(true);
      const res = await getExpenseCategories();
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setExpenseCategories(list);
      if (showMessage) {
        message.success("Categories loaded");
      }
    } catch (err) {
      console.error("EXPENSE CATEGORY FETCH ERROR:", err);
      if (showMessage) {
        message.error("Failed to load categories");
      }
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
    fetchVendors();
    fetchExpenseCategories();
  }, []);

  useEffect(() => {
  window.addEventListener("unhandledrejection", (event) => {
    console.error("REAL ERROR =>", event.reason);
  });
}, []);

  useEffect(() => {
  if (items.length > 0) {
    const total = items.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0
    );

    form.setFieldsValue({
      total_amount: total,
    });
  }
}, [items, form]);

  //------------------AddItem------------
  const handleAddItem = async () => {
  try {
    const vals = await itemForm.validateFields();

    const amount = (vals.qty || 0) * (vals.rate || 0);

    const newItem = {
      temp_id: Date.now(),
      is_new: true,
      ...vals,
      amount,
    };

    if (editItemIdx !== null) {
      setItems((prev) =>
        prev.map((it, i) =>
          i === editItemIdx
            ? {
                ...newItem,
                id: it.id,
                temp_id: it.temp_id,
                is_new: it.is_new ?? !it.id,
              }
            : it
        )
      );
    } else {
      setItems((prev) => [...prev, newItem]);
    }

    itemForm.resetFields();
    setAddItemModal(false);
    setEditItemIdx(null);

  } catch (err) {
    console.log("Validation Error:", err);

    message.error("Please fill all required item fields");
  }
};

  // ---------------- STATS ----------------
  const stats = useMemo(() => {
  const total = expenses.reduce(
    (s, e) => s + Number(e.total_amount || 0),
    0
  );

  const paid = expenses
    .filter((e) => e.status === "paid")
    .reduce((s, e) => s + Number(e.total_amount || 0), 0);

  const unpaid = expenses
    .filter((e) => e.status === "unpaid")
    .reduce((s, e) => s + Number(e.total_amount || 0), 0);

  return { total, paid, unpaid, count: expenses.length };
}, [expenses]);

  // ---------------- FILTER ----------------
  const filteredExpenses = useMemo(() => {
    return expenses
      .filter((exp) => {
        const matchSearch =
          !filters.search ||
          exp.bill_no?.toLowerCase().includes(filters.search.toLowerCase()) ||
          exp.vendor_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
          exp.description?.toLowerCase().includes(filters.search.toLowerCase());

        const matchStatus =
          filters.status === "all" || exp.status === filters.status;

        const matchCategory =
          filters.category === "all" ||
          exp.category_name === filters.category;

        const matchDate =
          !filters.dateRange ||
          dayjs(exp.expense_date).isBetween(
            filters.dateRange[0],
            filters.dateRange[1],
            "day",
            "[]"
          );

        return matchSearch && matchStatus && matchCategory && matchDate;
      })
      .sort(
        (a, b) =>
          dayjs(b.expense_date).valueOf() -
          dayjs(a.expense_date).valueOf()
      );
  }, [expenses, filters]);


  //new
  const groupedExpenses = useMemo(() => {
  const groups = {};

  filteredExpenses.forEach((exp) => {
    const date = dayjs(exp.expense_date).format("dddd, DD MMMM YYYY");

    if (!groups[date]) {
      groups[date] = [];
    }

    groups[date].push(exp);
  });

  return groups;
}, [filteredExpenses]);

  // ---------------- SAVE (API PLACEHOLDER) ----------------
  const handleSave = async () => {
  if (editingExpense && !canUpdate) {
    message.error("No permission to update expense");
    return;
  }
  if (!editingExpense && !canCreate) {
    message.error("No permission to create expense");
    return;
  }
  try {
    const values = await form.validateFields();

    const preparedItems = items.map((item) => {
      const payloadItem = {
        name: item.name,
        qty: Number(item.qty || 0),
        unit: item.unit || null,
        rate: Number(item.rate || 0),
        amount: Number(item.amount || 0),
      };

      if (!item.is_new && item.id) {
        payloadItem.id = item.id;
      }

      return payloadItem;
    });
    const selectedCategory = expenseCategories.find(
      (category) => category?.name === values.category_name
    );
    const selectedVendor = vendors.find(
      (vendor) => vendor?.name === values.vendor_name
    );

    const payload = {
      bill_no:
        editingExpense?.bill_no ||
        `BILL-${dayjs().format("YYYYMMDDHHmmssSSS")}`,
      expense_type: values.expense_type || null,
      category_id: selectedCategory?.id || null,
      vendor_name: values.vendor_name,
      vendor_id: getVendorPrimaryId(selectedVendor),
      category_name: values.category_name,
      description: values.description,
      add_to_food_stock: values.add_to_food_stock || false,
      expense_date: values.expense_date?.format("YYYY-MM-DD"),
      status: values.status,
      paid_by: values.status === "paid" ? values.paid_by || "" : null,
      paid_date:
        values.status === "paid" && values.paid_date
          ? values.paid_date.format("YYYY-MM-DD")
          : null,
      total_amount:
          items.length > 0
            ? items.reduce((sum, i) => sum + Number(i.amount || 0), 0)
            : Number(values.total_amount || 0),
      items: preparedItems,
    };

    if (editingExpense) {
      const res = await editExpense({
        id: editingExpense.id,
        ...payload,
      });
      if (!res?.data?.status) {
        message.error(res?.data?.message || "Failed to update expense");
        return;
      }
    } else {
      const res = await createExpense(payload);
      if (!res?.data?.status) {
        message.error(res?.data?.message || "Failed to create expense");
        return;
      }
    }

    message.success("Saved successfully");
    setDrawerOpen(false);
    form.resetFields();
    setItems([]); // RESET ITEMS
    setEditingExpense(null);
    await fetchExpenses();
  } catch (err) {
    message.error(err?.response?.data?.message || "Validation error");
  }
};

  // ---------------- DELETE ----------------
  const handleDelete = async (id) => {
    if (!canDelete) {
      message.error("No permission to delete expense");
      return;
    }
    try {
      const res = await deleteExpense(id);
      if (!res?.data?.status) {
        message.error(res?.data?.message || "Failed to delete");
        return;
      }
      message.success("Deleted");
      await fetchExpenses();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to delete");
    }
  };

  const toggleItemsView = (record) => {
    setExpandedRowKeys((prev) =>
      prev.includes(record.id)
        ? prev.filter((key) => key !== record.id)
        : [...prev, record.id]
    );
  };

  // ---------------- TABLE ----------------
  const columns = [
    { title: "Bill No", dataIndex: "bill_no" },
    { title: "Vendor", dataIndex: "vendor_name" },
    { title: "Category", dataIndex: "category_name" },
    {
      title: "Amount",
      dataIndex: "total_amount",
      render: (v) => `₹${v}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => (
        <Tag color={v === "paid" ? "green" : "orange"}>{v}</Tag>
      ),
    },
    {
  title: "Actions",
  render: (_, record) => (
    <Space>
      {/* EDIT */}
      <Button
        icon={<EditOutlined />}
        onClick={() => {
          setEditingExpense(record);
          setDrawerOpen(true);

          form.setFieldsValue({
            ...record,
            expense_date: record.expense_date
              ? dayjs(record.expense_date)
              : null,
          });

          setItems(record.items || []);
        }}
      />

      {/*  VIEW BUTTON  */}
      <Button
        icon={<SearchOutlined />}
        onClick={() => {
          Modal.info({
            title: `Items - ${record.bill_no}`,
            width: 700,
            content: (
              <Table
                dataSource={record.items || []}
                rowKey="id"
                pagination={false}
                columns={[
                  { title: "Item", dataIndex: "name" },
                  { title: "Qty", dataIndex: "qty" },
                  { title: "Unit", dataIndex: "unit" },
                  { title: "Rate", dataIndex: "rate" },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    render: (v) => `₹${v}`,
                  },
                ]}
              />
            ),
          });
        }}
      />

      {/* DELETE */}
      <Button
        danger
        icon={<DeleteOutlined />}
        onClick={() => handleDelete(record.id)}
      />
    </Space>
  ),
},
  ];

  if (!canView) {
    return (
      <Card>
        <Empty description="No permission to view in-house expenses" />
      </Card>
    );
  }

  return (
    <Card
      title={
            <div>
              <div style={{ fontWeight: 600, fontSize: 20 }}>
                In House Expenses
              </div>
              <div style={{ fontSize: 12, color: "#888" }}>
                Track and organize your expenses
              </div>
            </div>
          }
      extra={
        <Space>
             <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                    fetchExpenses(true);
                    fetchVendors(true);
                    fetchExpenseCategories(true);
                }}
                >
                Load
                </Button>
        
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={!canCreate}
          onClick={() => {
            if (!canCreate) return;
            setEditingExpense(null);
            setDrawerOpen(true);
            form.resetFields();
            setItems([]);
          }}
        >
          Add Expense
        </Button>
        </Space>
      }
    >
      {/* ---------------- STATS ---------------- */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={6}>
            <Card>
              <Space align="center">
                <ReloadOutlined style={{ fontSize: 24, color: "#1677ff" }} />
                <div>
                  <div>Total</div>
                  <b>₹{stats.total}</b>
                </div>
              </Space>
            </Card>
          </Col>

          <Col span={6}>
            <Card>
              <Space align="center">
                <CheckCircleOutlined style={{ fontSize: 24, color: "green" }} />
                <div>
                  <div>Paid</div>
                  <b>₹{stats.paid}</b>
                </div>
              </Space>
            </Card>
          </Col>

          <Col span={6}>
            <Card>
              <Space align="center">
                <ClockCircleOutlined style={{ fontSize: 24, color: "orange" }} />
                <div>
                  <div>Unpaid</div>
                  <b>₹{stats.unpaid}</b>
                </div>
              </Space>
            </Card>
          </Col>

          <Col span={6}>
            <Card>
              <Space align="center">
                <EyeOutlined style={{ fontSize: 24, color: "#722ed1" }} />
                <div>
                  <div>Bill Count</div>
                  <b>{stats.count}</b>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>

      {/* ---------------- FILTER ---------------- */}
      <Card style={{ marginBottom: 15 }}>
        <Row gutter={12}>
          <Col span={6}>
            <Input
              placeholder="Search..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
            />
          </Col>

          <Col span={4}>
            <Select
              value={filters.status}
              onChange={(v) =>
                setFilters({ ...filters, status: v })
              }
              style={{ width: "100%" }}
            >
              <Option value="all">All</Option>
              <Option value="paid">Paid</Option>
              <Option value="unpaid">Unpaid</Option>
            </Select>
          </Col>

          <Col span={4}>
            <Select
              value={filters.category}
              onChange={(v) =>
                setFilters({ ...filters, category: v })
              }
              style={{ width: "100%" }}
            >
              <Option value="all">All Category</Option>
              {expenseCategories.map((category) => (
                <Option key={category.id} value={category.name}>
                  {category.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={6}>
            <RangePicker
             showTime
              format="DD MMM YYYY • hh:mm A"
              style={{ width: "100%" }}
              value={filters.dateRange}
              onChange={(v) =>
                setFilters({ ...filters, dateRange: v })
              }
            />
          </Col>

          <Col span={4}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() =>
                setFilters({
                  search: "",
                  status: "all",
                  category: "all",
                  dateRange: null,
                })
              }
              block
            >
              Clear
            </Button>
          </Col>
        </Row>
      </Card>

      {/* ---------------- TABLE ---------------- */}
      {Object.entries(groupedExpenses).map(([date, list]) => {
      const dayTotal = list.reduce(
      (sum, e) => sum + Number(e.total_amount || 0),
      0
    );
  const paidCount = list.filter(e => e.status === "paid").length;
  const unpaidCount = list.filter(e => e.status === "unpaid").length;

  return (
    <Card key={date} style={{ marginBottom: 16, background: "#f5f7fa" }}>
      
      {/* ===== DATE HEADER ===== */}
      <Row justify="space-between" align="middle">
        <Col>
          <b>📅 {date}</b> &nbsp;
          <Tag color="gold">{list.length} bills</Tag>
        </Col>

        <Col>
          <Tag color="green">{paidCount} paid</Tag>
          <Tag color="orange">{unpaidCount} unpaid</Tag>
          <b style={{ marginLeft: 10 }}>₹{dayTotal}</b>
        </Col>
      </Row>

      {/* ===== INNER TABLE ===== */}
      <Table
        style={{ marginTop: 10 }}
        dataSource={list}
        loading={expensesLoading}
        pagination={false}
        rowKey="id"
        expandable={{
          expandedRowKeys,
          onExpand: (expanded, record) => {
            setExpandedRowKeys((prev) =>
              expanded
                ? [...prev, record.id]
                : prev.filter((key) => key !== record.id)
            );
          },
          expandedRowRender: (record) => {
            const expenseItems = Array.isArray(record.items) ? record.items : [];
            const totalAmount = expenseItems.reduce(
              (sum, item) => sum + Number(item?.amount || 0),
              0
            );

            return (
              <div
                style={{
                  background: "#fafafa",
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  padding: 16,
                }}
              >
                <Row gutter={[16, 8]} style={{ marginBottom: 8 }}>
                  <Col xs={24} sm={12} md={6}>
                    <Text type="secondary">Vendor: </Text>
                    <Text strong>{record.vendor_name || "-"}</Text>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Text type="secondary">Category: </Text>
                    <Tag color="green" style={{ marginInlineStart: 4 }}>
                      {record.category_name || "-"}
                    </Tag>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Text type="secondary">Paid By: </Text>
                    <Text strong>{record.paid_by || "-"}</Text>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Text type="secondary">Paid Date: </Text>
                    <Text strong>
                    {record.paid_date ? (
                      <div>
                        <div style={{ fontWeight: 600 }}>
                          {dayjs(record.paid_date).format(
                            "DD MMM YYYY"
                          )}
                        </div>

                       {/* <div
                          style={{
                            fontSize: 12,
                            color: "#888",
                          }}
                        >
                          {dayjs(record.paid_date).format(
                            "hh:mm A"
                          )}
                        </div>   */}
                      </div>
                    ) : (
                      "-"
                    )}
                  </Text>
                  </Col>
                </Row>

                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary">Description: </Text>
                  <Text strong>{record.description || "-"}</Text>
                </div>

                <Table
                  size="small"
                  bordered
                  dataSource={expenseItems}
                  rowKey="id"
                  pagination={false}
                  columns={[
                     {
                      title: "Sl No",
                      width: 80,
                      render: (_, __, index) => index + 1,
                    },
                    { title: "Item Description", dataIndex: "name" },
                    { title: "Unit", dataIndex: "unit", width: 110 },
                    { title: "Qty", dataIndex: "qty", width: 90 },
                    {
                      title: "Rate",
                      dataIndex: "rate",
                      width: 110,
                      render: (v) => `₹${v || 0}`,
                    },
                    {
                      title: "Amount",
                      dataIndex: "amount",
                      width: 130,
                      render: (v) => (
                        <Text strong style={{ color: "#1677ff" }}>
                          ₹{v || 0}
                        </Text>
                      ),
                    },
                  ]}
                  summary={() => (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={4}>
                          <Text strong>Total</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>
                          <Text strong style={{ color: "#52c41a" }}>
                            ₹{totalAmount}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                />
              </div>
            );
          },
          rowExpandable: (record) =>
            Array.isArray(record.items) && record.items.length > 0,
          expandIcon: () => null,
        }}
        columns={[
          { title: "Bill No", dataIndex: "bill_no" },
          { title: "Category", dataIndex: "category_name" },
          { title: "Vendor", dataIndex: "vendor_name" },
          { title: "Description", dataIndex: "description" },
          {
            title: "Amount",
            dataIndex: "total_amount",
            render: (v) => <b style={{ color: "#1677ff" }}>₹{v}</b>,
          },
          {
            title: "Status",
            dataIndex: "status",
            render: (v) => (
              <Tag color={v === "paid" ? "green" : "orange"}>
                {v.toUpperCase()}
              </Tag>
            ),
          },
          {
            title: "Actions",
            render: (_, record) => (
              <Space>
                <Button
                  icon={<EyeOutlined />}
                  disabled={!Array.isArray(record.items) || record.items.length === 0}
                  onClick={() => toggleItemsView(record)}
                />
                <Button
                  icon={<EditOutlined />}
                  disabled={!canUpdate}
                  onClick={() => {
                    setEditingExpense(record);
                    setDrawerOpen(true);
                    form.setFieldsValue({
                      ...record,
                      expense_date: record.expense_date
                        ? dayjs(record.expense_date)
                        : null,
                      paid_date: record.paid_date
                        ? dayjs(record.paid_date)
                        : null,
                    });
                    setItems(record.items || []);
                  }}
                />
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!canDelete}
                  onClick={() => handleDelete(record.id)}
                />
              </Space>
            ),
          },
        ]}
      />

      {/* ===== DAY TOTAL ===== */}
      <Row justify="end" style={{ marginTop: 10 }}>
        <b style={{ color: "green" }}>Day Total: ₹{dayTotal}</b>
      </Row>
    </Card>
  );
})}

      {/* ---------------- DRAWER ---------------- */}
      <Drawer
  title={
    <Space>
      <PlusOutlined style={{ color: "#1677ff" }} />
      <span>New Expense</span>
    </Space>
  }
  width={850}
  open={drawerOpen}
  onClose={() => setDrawerOpen(false)}
  extra={
    <Button type="primary" onClick={() => setVendorModalOpen(true)} disabled={!canCreate}>
      Add Vendor
    </Button>
  }
  footer={
    <Space style={{ float: "right" }}>
      <Button onClick={() => setDrawerOpen(false)}>
        Cancel
      </Button>
      <Button type="primary" onClick={handleSave} disabled={!canCreate && !canUpdate}>
        + Create Expense
      </Button>
    </Space>
  }
>
  
<Form form={form} layout="vertical">

    
    {/* ================= BASIC INFO ================= */}
    <Divider orientation="left">Basic Info</Divider>

    <Row gutter={16}>
      <Col span={12}>
        <Form.Item name="expense_date" label="Expense Date">
          <DatePicker 
            
             format="DD MMM YYYY "
          style={{ width: "100%" }}
          
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item name="category_name" label="Category">
          <Select
            placeholder="Select category"
            loading={categoriesLoading}
            showSearch
            optionFilterProp="label"
            options={expenseCategories
              .map((category) => ({
                value: category?.name || "",
                label: category?.name || "Unnamed Category",
              }))
              .filter((option) => option.value)}
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item name="vendor_name" label="Vendor ">
          <Select
            placeholder="Select Vendor"
            loading={vendorsLoading}
            showSearch
            optionFilterProp="label"
            options={vendors
              .map((vendor) => ({
                value: vendor?.name || "",
                label: vendor?.name || "Unnamed Vendor",
              }))
              .filter((option) => option.value)}
          />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item name="status" label="Payment Status">
          <Select>
            <Option value="paid">Paid</Option>
            <Option value="unpaid">Unpaid</Option>
          </Select>
        </Form.Item>
      </Col>

      {paymentStatus === "paid" && (
        <>
          <Col span={12}>
            <Form.Item
              name="paid_date"
              label="Paid Date"
              rules={[{ required: true, message: "Please select paid date" }]}
            >
              <DatePicker 
                
                  format="DD MMM YYYY "
              style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="paid_by"
              label="Paid By"
              rules={[{ required: true, message: "Please enter paid by" }]}
            >
              <Input placeholder="Enter person name" />
            </Form.Item>
          </Col>
        </>
      )}

      <Col span={24}>
        <Form.Item name="description" label="Description">
          <Input.TextArea
            rows={2}
            placeholder="Brief description of expense"
          />
        </Form.Item>
      </Col>
      <Col span={24}>
      <Form.Item
        name="add_to_food_stock"
        valuePropName="checked"
      >
        <Checkbox>
          Add items to Food Stock
        </Checkbox>
      </Form.Item>
    </Col>
    </Row>

    {/* ================= ITEMS ================= */}
    <Divider orientation="left">Expense Items</Divider>

    <div style={{ marginBottom: 12 }}>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => {
          itemForm.resetFields();
          setEditItemIdx(null);
          setAddItemModal(true);
        }}
      >
        Add Item
      </Button>
    </div>

    <div
      style={{
        border: "1px dashed #d9d9d9",
        padding: 20,
        textAlign: "center",
        borderRadius: 8,
        color: "#888",
      }}
    >
      {items.length > 0 ? (
        <Table
          size="small"
          dataSource={items}
          pagination={false}
          rowKey={(row, index) => row.id || row.temp_id || index}
          columns={[
            { title: "Item Description", dataIndex: "name" },
            { title: "Qty", dataIndex: "qty" },
            { title: "Unit", dataIndex: "unit" },
            { title: "Rate", dataIndex: "rate" },
            {
              title: "Amount",
              dataIndex: "amount",
              render: (v) => `₹${v}`,
            },
            {
              title: "Action",
              render: (_, row, idx) => (
                <Space>
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditItemIdx(idx);
                      itemForm.setFieldsValue(row);
                      setAddItemModal(true);
                    }}
                  />
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      setItems((prev) => prev.filter((_, i) => i !== idx))
                    }
                  />
                </Space>
              ),
            },
          ]}
        />
      ) : (
        <div
          style={{
            border: "1px dashed #d9d9d9",
            padding: 20,
            textAlign: "center",
            borderRadius: 8,
          }}
        >
          No items added. Click "Add Item"
        </div>
      )}
    </div>

    {/* ================= TOTAL ================= */}
    <Divider />

    <Form.Item name="total_amount" label="Total Amount (₹)">
      <InputNumber
        style={{ width: "100%" }}
        prefix="₹"
        min={0}
        placeholder="Enter total amount"
      />
    </Form.Item>

  </Form>
</Drawer>
        <Modal
        title={editItemIdx !== null ? "Edit Expense Item" : "Add Expense Item"}
        open={addItemModal}
        onOk={handleAddItem}
        onCancel={() => {
          setAddItemModal(false);
          setEditItemIdx(null);
          itemForm.resetFields();
        }}
      >
        <Form form={itemForm} layout="vertical">
          <Form.Item
            name="name"
            label="Item Description"
            rules={[{ required: true, message:"Item Description is required" }]}
          >
            <Input placeholder="e.g. electricity units consumed" />
          </Form.Item>

          <Row gutter={12}>
          <Col span={8}>
            <Form.Item
            name="qty"
            label="Quantity"
            rules={[
              { required: true, message: "Please enter quantity" },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              placeholder="Qty"
            />
          </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
            name="unit"
            label="Unit"
            rules={[
              { required: true, message: "Please enter unit" },
            ]}
          >
            <Input placeholder="kg / ltr / pcs" />
          </Form.Item>
          </Col>

          <Col span={8}>
           <Form.Item
            name="rate"
            label="Rate"
            rules={[
              { required: true, message: "Please enter rate" },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              placeholder="Rate"
            />
          </Form.Item>
          </Col>
        </Row>
        </Form>
         
      </Modal>

      <Modal
  title="Add Vendor"
  open={vendorModalOpen}
  onCancel={() => {
    setVendorModalOpen(false);
    vendorForm.resetFields();
  }}
  onOk={async () => {
    try {
      const values = await vendorForm.validateFields();

      const payload = {
        name: values.name || "",
        phone: values.phone || "",
        address: values.address || "",
        category: values.category || "",
        pan: values.pan || "",
        aadhar: values.aadhar || "",
        gst_number: values.gst_number || "",
        status: values.status || "Active",
      };

      const res = await createVendor(payload);

      if (!res?.data) {
        message.error("Failed to create vendor");
        return;
      }

      message.success("Vendor added successfully");

      setVendorModalOpen(false);
      vendorForm.resetFields();

      // 🔥 refresh vendor dropdown
      fetchVendors();

    } catch (err) {
      message.error("Please fill required fields");
    }
  }}
>
  <Form form={vendorForm} layout="vertical">
    
    <Form.Item
      name="name"
      label="Vendor Name"
      rules={[{ required: true, message: "Enter vendor name" }]}
    >
      <Input />
    </Form.Item>

    <Form.Item name="phone" label="Phone">
      <Input />
    </Form.Item>

    <Form.Item name="address" label="Address">
      <Input />
    </Form.Item>

    <Form.Item name="category" label="Vendor Type">
      <Select placeholder="Select type">
        <Option value="Rice">Rice</Option>
        <Option value="Drinks">Drinks</Option>
        <Option value="Vegetables">Vegetables</Option>
        <Option value="Dairy">Dairy</Option>
        <Option value="Meat">Meat</Option>
        <Option value="General">General</Option>
      </Select>
    </Form.Item>

    <Form.Item name="pan" label="PAN">
      <Input />
    </Form.Item>

    <Form.Item name="aadhar" label="Aadhaar">
      <Input />
    </Form.Item>

    <Form.Item name="gst_number" label="GST">
      <Input />
    </Form.Item>

    <Form.Item name="status" label="Status" initialValue="Active">
      <Select>
        <Option value="Active">Active</Option>
        <Option value="Inactive">Inactive</Option>
      </Select>
    </Form.Item>

  </Form>
</Modal>
    </Card>
  );
}

